import React, { FC, useCallback, useEffect, useMemo, useState } from "react";

import Button from "../../../../components/button/Button";
import { WorkDetailsModel } from "../../../../factories/work/model/work-details.model";
import { TimePeriod } from "../../../../hooks/useDetectTimePeriod";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import {
  useFilterTimeQuery,
  useGetBilledHoursByWorkIdQuery,
} from "../../../../slices/UserSlice";
import { workTimeTabButton } from "../../../main/timer/timerTarget";
import { useTimerTarget } from "../../../main/timer/useTimerTarget";
import { Time } from "../../../time/InvoiceTime";
import { createDefaultTimeFilter } from "../../../time/time-filter.utils";
import { TimeFilterKey } from "../../../time/TimeFilters";
import { TimesListView } from "../../../time/TimesListView";
import { TimeColumnKey } from "../../../time/types/time-list";

const TIME_PAGE_SIZE = 20;

type Props = {
  work: WorkDetailsModel;
};

const WORK_TIME_VISIBLE_FILTERS: TimeFilterKey[] = [
  TimeFilterKey.Date,
  TimeFilterKey.Assignee,
  TimeFilterKey.Status,
];

const WORK_TIME_VISIBLE_COLUMNS: TimeColumnKey[] = [
  TimeColumnKey.TotalTime,
  TimeColumnKey.Date,
  TimeColumnKey.Total,
  TimeColumnKey.Status,
  TimeColumnKey.Assignee,
  TimeColumnKey.Notes,
  TimeColumnKey.Type,
];

export const WorkTime: FC<Props> = ({ work }) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const workId = work?.id || "";

  const { timer, startWorkTimer, toggleActiveTimer } = useTimerTarget();

  const [filter, setFilter] = useState<Time>(
    createDefaultTimeFilter({ orgId }),
  );
  const [page, setPage] = useState(1);

  const timeQueryArgs = useMemo(
    () => ({
      filterByClient: filter.filterByClient.map((item) => item.value),
      filterByAssignee: filter.filterByAssignee.map((item) => item.value),
      filterByWorkType: filter.filterByWorkType.map((item) => item.value),
      filterByStatus: filter.filterByStatus,
      filterByBillable: filter.filterByBillable ?? null,
      filterByDate:
        filter.filterByDate?.[0] === TimePeriod.All ? [] : filter.filterByDate,
      fromDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.fromDate?.toISOString(),
      toDate:
        filter.filterByDate?.[0] === TimePeriod.All
          ? null
          : filter.toDate?.toISOString(),
      orgId,
      sortOrder: filter.sortOrder,
      sortBy: filter.sortBy,
      filterByWork: workId ? [workId] : [],
      filterByWorkTask: [],
      pageNumber: page,
      pageSize: TIME_PAGE_SIZE,
    }),
    [filter, orgId, page, workId],
  );

  const {
    data: filterTime,
    isLoading: isLoadingTime,
    isFetching: isFetchingTime,
    refetch: refetchTime,
  } = useFilterTimeQuery(timeQueryArgs, {
    skip: !orgId || !workId,
  });

  const {
    data: billableHours,
    isLoading: isLoadingBillableHours,
    isFetching: isFetchingBillableHours,
    refetch: refetchBilledHours,
  } = useGetBilledHoursByWorkIdQuery(
    { orgId, workId },
    {
      skip: !orgId || !workId,
    },
  );

  const getData = useCallback(() => {
    if (!orgId || !workId) {
      return;
    }

    void refetchTime();
    void refetchBilledHours();
  }, [orgId, refetchBilledHours, refetchTime, workId]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const buttonTimerVariant = () => {
    // Pause/Continue only for this work item's own timer (QA A5).
    const state = workTimeTabButton(timer, workId);

    if (state === "start" || state === "blocked") {
      return (
        <span
          title={
            state === "blocked"
              ? "Another timer is running. Stop it first to start one here."
              : undefined
          }>
          <Button
            onClick={() => startWorkTimer({ work })}
            buttonType={"button"}
            label={"Start timer"}
            disabled={state === "blocked"}
            extraClasses={"normal-case"}
          />
        </span>
      );
    }

    return (
      <Button
        onClick={toggleActiveTimer}
        buttonType={"button"}
        label={state === "continue" ? "Continue" : "Pause"}
        extraClasses={"normal-case"}
      />
    );
  };

  return (
    <TimesListView
      billableHours={billableHours}
      buttonTimerVariant={buttonTimerVariant}
      filterTime={filterTime?.items}
      getData={getData}
      filter={filter}
      setFilter={setFilter}
      visibleFilters={WORK_TIME_VISIBLE_FILTERS}
      visibleColumns={WORK_TIME_VISIBLE_COLUMNS}
      isLoading={
        isLoadingTime ||
        isFetchingTime ||
        isLoadingBillableHours ||
        isFetchingBillableHours
      }
      page={filterTime?.pageNumber ?? page}
      totalPages={filterTime?.totalPages ?? 1}
      total={filterTime?.totalCount}
      pageSize={filterTime?.pageSize || TIME_PAGE_SIZE}
      onPageChange={setPage}
    />
  );
};
